import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronRight, ChevronDown, BookOpen, Plus, Edit3, Trash2, Save, Search, X, Image, Link, Paperclip, GripVertical } from 'lucide-react';
import toast from 'react-hot-toast';


// Компонент для встраивания видео
const VideoEmbed = ({ url, title = "Видео" }) => {
  const getVideoEmbed = (url) => {
    // YouTube
    if (url.includes('youtube.com/watch') || url.includes('youtu.be/')) {
      const videoId = url.includes('youtu.be/') 
        ? url.split('youtu.be/')[1]?.split('?')[0]
        : url.split('v=')[1]?.split('&')[0];
      
      if (videoId) {
        return `<iframe 
          width="560" 
          height="315" 
          src="https://www.youtube.com/embed/${videoId}" 
          title="${title}"
          frameborder="0" 
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
          allowfullscreen
          class="w-full max-w-2xl mx-auto rounded-lg shadow-lg"
          style="aspect-ratio: 16/9; max-width: 100%; height: auto;"
        ></iframe>`;
      }
    }
    
    // VK Video
    if (url.includes('vk.com/video')) {
      return `<iframe 
        src="${url.replace('vk.com/video', 'vk.com/video_ext.php')}" 
        width="640" 
        height="360" 
        frameborder="0" 
        allowfullscreen
        class="w-full max-w-2xl mx-auto rounded-lg shadow-lg"
        style="aspect-ratio: 16/9; max-width: 100%; height: auto;"
      ></iframe>`;
    }
    
    // Rutube
    if (url.includes('rutube.ru/video/')) {
      const videoId = url.split('rutube.ru/video/')[1]?.split('/')[0];
      if (videoId) {
        return `<iframe 
          width="720" 
          height="405" 
          src="https://rutube.ru/play/embed/${videoId}" 
          frameborder="0" 
          webkitAllowFullScreen 
          mozallowfullscreen 
          allowfullscreen
          class="w-full max-w-2xl mx-auto rounded-lg shadow-lg"
          style="aspect-ratio: 16/9; max-width: 100%; height: auto;"
        ></iframe>`;
      }
    }
    
    // TikTok
    if (url.includes('tiktok.com')) {
      return `<blockquote 
        class="tiktok-embed" 
        cite="${url}" 
        data-video-id="${url.split('/video/')[1]?.split('?')[0]}"
        style="max-width: 605px; min-width: 325px;"
      >
        <section></section>
      </blockquote>
      <script async src="https://www.tiktok.com/embed.js"></script>`;
    }
    
    // Dailymotion
    if (url.includes('dailymotion.com/video/')) {
      const videoId = url.split('dailymotion.com/video/')[1]?.split('?')[0];
      if (videoId) {
        return `<iframe 
          frameborder="0" 
          width="560" 
          height="315" 
          src="https://www.dailymotion.com/embed/video/${videoId}" 
          allowfullscreen 
          allow="autoplay"
          class="w-full max-w-2xl mx-auto rounded-lg shadow-lg"
          style="aspect-ratio: 16/9; max-width: 100%; height: auto;"
        ></iframe>`;
      }
    }
    
    // Обычная ссылка на видео
    return `<div class="my-4 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-2">🎥 Видео:</p>
      <a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 dark:text-blue-400 hover:underline">
        ${title}
      </a>
    </div>`;
  };

  return (
    <div 
      className="my-4 flex justify-center"
      dangerouslySetInnerHTML={{ __html: getVideoEmbed(url) }}
    />
  );
};

// Простой компонент для отображения текста с изображениями и видео
const SimpleContentRenderer = ({ content }) => {
  if (!content) {
    return null;
  }
  
  // Обрабатываем видео ссылки
  let processedContent = content
    .replace(/\[([^\]]*)\]\((https?:\/\/[^)]+)\)/g, (match, text, url) => {
      // Проверяем, является ли это ссылкой на видео
      if (url.includes('youtube.com') || url.includes('youtu.be') || 
          url.includes('vk.com/video') || url.includes('vkvideo.ru') || 
          url.includes('rutube.ru') || url.includes('tiktok.com') || 
          url.includes('dailymotion.com')) {
        return `[VIDEO:${url}:${text}]`;
      }
      return match; // Обычная ссылка
    });
  
  // Обрабатываем изображения
  processedContent = processedContent
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, url) => {
      // Если это локальный путь, добавляем префикс API
      const imageUrl = url.startsWith('/uploads/') ? `/api/upload/images/${url.split('/').pop()}` : url;
      
      const imageHtml = `<div class="my-4 flex justify-center">
        <img src="${imageUrl}" alt="${alt}" class="max-w-full h-auto rounded-lg shadow-sm border border-gray-200 dark:border-gray-600" style="max-height: 400px;" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';" />
        <div style="display:none;" class="text-center text-gray-500 dark:text-gray-400 p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
          <div class="text-sm">Не удалось загрузить изображение</div>
          <div class="text-xs mt-1 text-gray-400">${imageUrl}</div>
        </div>
      </div>`;
      
      return imageHtml;
    });
  
  // Обрабатываем видео
  processedContent = processedContent
    .replace(/\[VIDEO:(https?:\/\/[^:]+):([^\]]+)\]/g, (match, url, title) => {
      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        const videoId = url.includes('youtu.be/') 
          ? url.split('youtu.be/')[1]?.split('?')[0]
          : url.split('v=')[1]?.split('&')[0];
        
        if (videoId) {
          const iframeHtml = `<div class="my-4 flex justify-center">
            <iframe 
              width="560" 
              height="315" 
              src="https://www.youtube.com/embed/${videoId}" 
              title="${title}"
              frameborder="0" 
              allowfullscreen
              class="w-full max-w-2xl mx-auto rounded-lg shadow-lg"
              style="aspect-ratio: 16/9; max-width: 100%; height: auto;"
            ></iframe>
          </div>`;
          return iframeHtml;
        }
      }
      
      if (url.includes('vk.com/video') || url.includes('vkvideo.ru')) {
        let vkUrl = url;
        if (url.includes('vk.com/video')) {
          vkUrl = url.replace('vk.com/video', 'vk.com/video_ext.php');
        } else if (url.includes('vkvideo.ru')) {
          vkUrl = url.replace('vkvideo.ru', 'vk.com/video_ext.php');
        }
        
        return `<div class="my-4 flex justify-center">
          <iframe 
            src="${vkUrl}" 
            width="640" 
            height="360" 
            frameborder="0" 
            allowfullscreen
            class="w-full max-w-2xl mx-auto rounded-lg shadow-lg"
            style="aspect-ratio: 16/9; max-width: 100%; height: auto;"
          ></iframe>
        </div>`;
      }
      
      if (url.includes('rutube.ru/video/')) {
        const videoId = url.split('rutube.ru/video/')[1]?.split('/')[0];
        if (videoId) {
          return `<div class="my-4 flex justify-center">
            <iframe 
              width="720" 
              height="405" 
              src="https://rutube.ru/play/embed/${videoId}" 
              frameborder="0" 
              webkitAllowFullScreen 
              mozallowfullscreen 
              allowfullscreen
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
              class="w-full max-w-2xl mx-auto rounded-lg shadow-lg"
              style="aspect-ratio: 16/9; max-width: 100%; height: auto;"
            ></iframe>
          </div>`;
        }
      }
      
      return match;
    });
  
  // Заменяем переносы строк на <br>, но только в тексте (не внутри HTML тегов)
  processedContent = processedContent.replace(/\n(?![^<]*>)/g, '<br>');
  
  
  return (
    <div 
      className="prose max-w-none text-gray-700 dark:text-gray-300 leading-relaxed"
      dangerouslySetInnerHTML={{ __html: processedContent }}
    />
  );
};

// Компонент для перетаскиваемого элемента
const SortableItem = ({ item, level, index, onEdit, onDelete, onAddSubItem, onSelect, onToggleExpanded, isExpanded, hasChildren, isSelected, renderMenuItem }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div>
      <div
        ref={setNodeRef}
        style={style}
        className={`py-2 px-3 cursor-pointer hover:bg-gray-100 rounded-lg mb-1 flex items-center justify-between ${
          isSelected ? 'bg-primary-100 text-primary-700' : 'text-gray-400'
        } ${isDragging ? 'bg-blue-100 shadow-lg' : ''} ${level > 0 ? 'ml-4' : ''}`}
        onClick={() => {
          onSelect(item);
          if (hasChildren) {
            onToggleExpanded(item._id);
          }
        }}
      >
        <div className="flex items-center space-x-2">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
            <GripVertical className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          </div>
          {hasChildren && (
            isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
          )}
          <span className="font-medium" dangerouslySetInnerHTML={{ __html: item.title }} />
        </div>
        <div className="flex space-x-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddSubItem(item._id);
            }}
            className="text-green-600 hover:text-green-700 p-1"
            title="Добавить подраздел"
          >
            <Plus className="h-3 w-3" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(item);
            }}
            className="text-orange-600 hover:text-orange-700 p-1"
            title="Редактировать раздел"
          >
            <Edit3 className="h-3 w-3" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item._id);
            }}
            className="text-red-600 hover:text-red-700 p-1"
            title="Удалить раздел"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
      
      {/* Рендерим дочерние элементы если они есть и элемент развернут */}
      {isExpanded && hasChildren && item.children && (
        <div className="ml-4">
          {item.children.filter(child => child && child._id).map(child => renderMenuItem(child, level + 1))}
        </div>
      )}
    </div>
  );
};

const LiveEditManual = () => {
  const { id } = useParams();
  const [manual, setManual] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [editingItem, setEditingItem] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [editingTitle, setEditingTitle] = useState('');
  const [editingItemTitle, setEditingItemTitle] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItemData, setNewItemData] = useState({ title: '', content: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [items, setItems] = useState([]);
  const [editingManual, setEditingManual] = useState(false);
  const [editingManualTitle, setEditingManualTitle] = useState(false);
  const [manualForm, setManualForm] = useState({ title: '', description: '', isPublic: false });
  const [showImageModal, setShowImageModal] = useState(false);
  const [mediaType, setMediaType] = useState('image'); // 'image' или 'video'
  const [imageUrl, setImageUrl] = useState('');
  const [previewContent, setPreviewContent] = useState('');
  const [selectedFont, setSelectedFont] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [showColorPalette, setShowColorPalette] = useState(false);
  const [showBgColorPalette, setShowBgColorPalette] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#000000');
  const [selectedBgColor, setSelectedBgColor] = useState('#ffffff');
  
  // Сенсоры для drag & drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Состояние для выбора активного поля
  const [activeField, setActiveField] = useState('content'); // 'title' или 'content'
  
  // Ref для поля названия мануала
  const manualTitleRef = useRef(null);

  useEffect(() => {
    fetchManual();
  }, [id]);

  // Инициализация содержимого поля названия при открытии режима редактирования
  useEffect(() => {
    if (editingManualTitle && manualTitleRef.current) {
      manualTitleRef.current.innerHTML = manualForm.title;
    }
  }, [editingManualTitle]);




  useEffect(() => {
    if (items.length > 0) {
      performSearch(searchQuery);
    }
  }, [searchQuery, items]);

  // Закрытие палитр цветов при клике вне их
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Проверяем, что клик не по элементам палитры цветов
      if (showColorPalette) {
        const colorPaletteElement = event.target.closest('.color-palette-container');
        const manualTitleColorPalette = event.target.closest('.manual-title-color-palette');
        if (!colorPaletteElement && !manualTitleColorPalette) {
          setShowColorPalette(false);
        }
      }
      
      // Проверяем, что клик не по элементам палитры цветов фона
      if (showBgColorPalette) {
        const bgColorPaletteElement = event.target.closest('.bg-color-palette-container');
        if (!bgColorPaletteElement) {
          setShowBgColorPalette(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColorPalette, showBgColorPalette]);

  // Обновляем предварительный просмотр при изменении содержимого
  useEffect(() => {
    setPreviewContent(editingContent);
  }, [editingContent]);

  // Обновляем содержимое редактора при изменении editingContent
  useEffect(() => {
    const editors = document.querySelectorAll('[contenteditable]');
    editors.forEach((editor, index) => {
      if (index === 1 && editor.innerHTML !== editingContent) { // Второй contentEditable - содержимое
        editor.innerHTML = editingContent;
      } else if (index === 0 && editor.innerHTML !== editingItemTitle) { // Первый contentEditable - заголовок
        editor.innerHTML = editingItemTitle;
      }
    });
  }, [editingContent, editingItemTitle]);

  // Обновляем заголовок и содержимое при загрузке раздела
  useEffect(() => {
    if (editingItem) {
      if (editingItem.title) {
        setEditingItemTitle(editingItem.title);
      }
      if (editingItem.content) {
        setEditingContent(editingItem.content);
      }
    }
  }, [editingItem]);

  // Функция для получения активного редактора
  const getActiveEditor = () => {
    // Если редактируем название мануала, используем ref
    if (activeField === 'title' && editingManualTitle && manualTitleRef.current) {
      return manualTitleRef.current;
    }
    
    const editors = document.querySelectorAll('[contenteditable]');
    return activeField === 'title' ? editors[0] : editors[1];
  };

  // Функция для сохранения позиции курсора
  const saveCursorPosition = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      return selection.getRangeAt(0);
    }
    return null;
  };

  // Функция для восстановления позиции курсора
  const restoreCursorPosition = (range) => {
    if (range) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  // Функция для умной очистки HTML - убирает лишние теги и вложенности
  const cleanFontTags = (html) => {
    if (!html || html.trim() === '') return '';
    
    return html
      // Конвертируем font теги в span
      .replace(/<font[^>]*color="([^"]*)"[^>]*>/gi, '<span style="color: $1">')
      .replace(/<font[^>]*style="[^"]*background-color:\s*([^;"]*)[^"]*"[^>]*>/gi, '<span style="background-color: $1">')
      .replace(/<\/font>/gi, '</span>')
      .replace(/<font[^>]*>/gi, '')
      .replace(/<\/font>/gi, '')
      // Убираем лишние пробелы и символы
      .replace(/&nbsp;/gi, ' ')
      // Убираем пустые span теги
      .replace(/<span[^>]*><\/span>/gi, '')
      // Убираем span с прозрачным фоном без содержимого
      .replace(/<span style="background-color:\s*transparent[^"]*"><\/span>/gi, '')
      // Убираем span с прозрачным фоном и содержимым - оставляем только содержимое
      .replace(/<span style="background-color:\s*transparent[^"]*">([^<]*)<\/span>/gi, '$1')
      // Убираем вложенные span с одинаковыми стилями
      .replace(/<span([^>]*)><span\1>/gi, '<span$1>')
      .replace(/<\/span><\/span>/gi, '</span>')
      // Убираем span с цветом по умолчанию (черный)
      .replace(/<span style="color:\s*rgb\(0,\s*0,\s*0\)[^"]*">([^<]*)<\/span>/gi, '$1')
      .replace(/<span style="color:\s*#000000[^"]*">([^<]*)<\/span>/gi, '$1')
      .replace(/<span style="color:\s*black[^"]*">([^<]*)<\/span>/gi, '$1')
      // Убираем span с белым фоном по умолчанию
      .replace(/<span style="background-color:\s*rgb\(255,\s*255,\s*255\)[^"]*">([^<]*)<\/span>/gi, '$1')
      .replace(/<span style="background-color:\s*#ffffff[^"]*">([^<]*)<\/span>/gi, '$1')
      .replace(/<span style="background-color:\s*white[^"]*">([^<]*)<\/span>/gi, '$1')
      // Убираем лишние стили форматирования
      .replace(/<span style="font-weight:\s*normal[^"]*">([^<]*)<\/span>/gi, '$1')
      .replace(/<span style="font-style:\s*normal[^"]*">([^<]*)<\/span>/gi, '$1')
      .replace(/<span style="text-decoration:\s*none[^"]*">([^<]*)<\/span>/gi, '$1')
      // Убираем span с только лишними стилями
      .replace(/<span style="font-weight:\s*normal;\s*">([^<]*)<\/span>/gi, '$1')
      .replace(/<span style="font-style:\s*normal;\s*">([^<]*)<\/span>/gi, '$1')
      .replace(/<span style="text-decoration:\s*none;\s*">([^<]*)<\/span>/gi, '$1')
      // Убираем пустые стили
      .replace(/<span style=";\s*">([^<]*)<\/span>/gi, '$1')
      .replace(/<span style="">([^<]*)<\/span>/gi, '$1')
      // Исправляем разорванные теги - убираем лишние закрывающие теги
      .replace(/<\/span>(?!<)/g, (match, offset, string) => {
        // Считаем количество открывающих и закрывающих span тегов до этой позиции
        const beforeMatch = string.substring(0, offset);
        const openTags = (beforeMatch.match(/<span[^>]*>/g) || []).length;
        const closeTags = (beforeMatch.match(/<\/span>/g) || []).length;
        // Если закрывающих тегов больше чем открывающих, убираем этот тег
        return closeTags >= openTags ? '' : match;
      })
      // Убираем лишние закрывающие теги в конце
      .replace(/<\/span>$/g, '')
      .replace(/<\/span>\s*$/g, '');
  };

  // Добавляем CSS стили для изображений в contentEditable полях
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      [contenteditable] img {
        max-width: 100% !important;
        height: auto !important;
        display: block !important;
        margin: 10px 0 !important;
        border-radius: 8px !important;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
        border: 1px solid #e5e7eb !important;
      }

      [contenteditable] img:hover {
        box-shadow: 0 4px 8px rgba(0,0,0,0.15) !important;
        transform: scale(1.02) !important;
        transition: all 0.2s ease !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // MutationObserver для отслеживания изменений в contentEditable полях
  useEffect(() => {
    const handleContentChange = () => {
      const editors = document.querySelectorAll('[contenteditable]');
      editors.forEach(editor => {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
              const addedNodes = Array.from(mutation.addedNodes);
              const hasImage = addedNodes.some(node =>
                node.nodeType === Node.ELEMENT_NODE &&
                (node.tagName === 'IMG' || node.querySelector('img'))
              );
              if (hasImage) {
                console.log('🖼️ Обнаружено добавление изображения, обновляем состояние');
                // Принудительно обновляем состояние
                const newContent = editor.innerHTML;
                if (activeField === 'content') {
                  setEditingContent(newContent);
                  setPreviewContent(newContent);
                } else {
                  setEditingTitle(newContent);
                }
              }
            }
          });
        });
        observer.observe(editor, {
          childList: true,
          subtree: true
        });
        editor._mutationObserver = observer;
      });
    };
    
    const timeoutId = setTimeout(handleContentChange, 100);
    
    return () => {
      clearTimeout(timeoutId);
      const editors = document.querySelectorAll('[contenteditable]');
      editors.forEach(editor => {
        if (editor._mutationObserver) {
          editor._mutationObserver.disconnect();
          delete editor._mutationObserver;
        }
      });
    };
  }, [activeField]);

  // Функция для применения форматирования к активному полю
  const applyFormatting = (command, value = false) => {
    const editor = getActiveEditor();
    console.log('🎨 Применяем форматирование:', command, value);
    console.log('🎨 Активное поле:', activeField);
    console.log('🎨 Редактируем название мануала:', editingManualTitle);
    console.log('🎨 Найденный редактор:', editor);
    
    if (editor) {
      editor.focus();
      
      // Получаем текущее выделение
      const selection = window.getSelection();
      
      // Для команд форматирования без выделения:
      // - bold, italic, underline - выделяем весь текст
      // - цвета и шрифты - НЕ выделяем, применяем к текущей позиции курсора
      if ((command === 'bold' || command === 'italic' || command === 'underline') &&
          (selection.rangeCount === 0 || selection.isCollapsed)) {
        const range = document.createRange();
        range.selectNodeContents(editor);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      
      // Отладка для команд списков
      if (command === 'insertUnorderedList' || command === 'insertOrderedList') {
        console.log('📝 Создание списка:', command);
        console.log('📝 Активный редактор:', editor);
        console.log('📝 Выделение:', selection.toString());
      }
      
      // Для цветов используем современный подход с span
      if (command === 'foreColor') {
        const range = selection.getRangeAt(0);
        if (!range.collapsed) {
          // Если есть выделение - применяем к выделенному тексту
          const span = document.createElement('span');
          span.style.color = value;
          try {
            range.surroundContents(span);
          } catch (e) {
            range.insertNode(span);
          }
        } else {
          // Если нет выделения - для названия мануала выделяем весь текст
          if (activeField === 'title' && editingManualTitle) {
            const fullRange = document.createRange();
            fullRange.selectNodeContents(editor);
            selection.removeAllRanges();
            selection.addRange(fullRange);
            
            // Теперь применяем цвет к выделенному тексту
            const span = document.createElement('span');
            span.style.color = value;
            try {
              fullRange.surroundContents(span);
            } catch (e) {
              fullRange.insertNode(span);
            }
          } else {
            // Для других полей - создаем span в позиции курсора для будущего ввода
            const span = document.createElement('span');
            span.style.color = value;
            span.innerHTML = '&nbsp;'; // Невидимый пробел для span
            range.insertNode(span);
            // Перемещаем курсор внутрь span
            range.setStartAfter(span);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      } else if (command === 'fontName') {
        const range = selection.getRangeAt(0);
        if (!range.collapsed) {
          // Если есть выделение - применяем к выделенному тексту
          const span = document.createElement('span');
          span.style.fontFamily = value;
          try {
            range.surroundContents(span);
          } catch (e) {
            range.insertNode(span);
          }
        } else {
          // Если нет выделения - создаем span в позиции курсора для будущего ввода
          const span = document.createElement('span');
          span.style.fontFamily = value;
          span.innerHTML = '&nbsp;'; // Невидимый пробел для span
          range.insertNode(span);
          // Перемещаем курсор внутрь span
          range.setStartAfter(span);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      } else if (command === 'backColor') {
        const range = selection.getRangeAt(0);
        if (!range.collapsed) {
          if (value === 'transparent') {
            // Для прозрачного фона - удаляем все span с background-color
            const selectedHTML = range.cloneContents();
            const walker = document.createTreeWalker(
              selectedHTML,
              NodeFilter.SHOW_ELEMENT,
              null,
              false
            );
            
            const elementsToClean = [];
            let node;
            while (node = walker.nextNode()) {
              if (node.style && node.style.backgroundColor) {
                elementsToClean.push(node);
              }
            }
            
            // Заменяем содержимое без span с фоном
            const tempDiv = document.createElement('div');
            tempDiv.appendChild(selectedHTML.cloneNode(true));
            
            elementsToClean.forEach(element => {
              const parent = element.parentNode;
              while (element.firstChild) {
                parent.insertBefore(element.firstChild, element);
              }
              parent.removeChild(element);
            });
            
            range.deleteContents();
            range.insertNode(tempDiv);
          } else {
            const span = document.createElement('span');
            span.style.backgroundColor = value;
            try {
              range.surroundContents(span);
            } catch (e) {
              range.insertNode(span);
            }
          }
        } else {
          // Если нет выделения - создаем span в позиции курсора для будущего ввода
          if (value === 'transparent') {
            // Для прозрачного фона ничего не делаем
            return;
          } else {
            const span = document.createElement('span');
            span.style.backgroundColor = value;
            span.innerHTML = '&nbsp;'; // Невидимый пробел для span
            range.insertNode(span);
            // Перемещаем курсор внутрь span
            range.setStartAfter(span);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      } else {
        // Для остальных команд используем стандартный execCommand
        console.log('📝 Выполняем execCommand:', command, value);
        const result = document.execCommand(command, false, value);
        console.log('📝 Результат execCommand:', result);
        
        // Отладка для списков - проверяем что создалось
        if (command === 'insertUnorderedList' || command === 'insertOrderedList') {
          console.log('📝 HTML после создания списка:', editor.innerHTML);
        }
      }
      
      // Обновляем состояние после применения форматирования
      if (activeField === 'title') {
        if (editingManualTitle) {
          // Если редактируем название мануала
          setManualForm({ ...manualForm, title: editor.innerHTML });
        } else {
          // Если редактируем заголовок раздела
          setEditingTitle(editor.innerHTML);
        }
      } else {
        setEditingContent(editor.innerHTML);
        setPreviewContent(editor.innerHTML);
      }
    }
  };


  const fetchManual = async () => {
    try {
      const isNew = !id || id === 'new';
      
      if (isNew) {
        console.log('🆕 Создание нового мануала');
        const newManual = { title: '', description: '', isPublic: false };
        setManual(newManual);
        setManualForm(newManual);
        setItems([]);
        setFilteredItems([]);
        setSelectedItem(null);
        setEditingManual(true);
      } else {
        console.log('🔍 Загружаем мануал для живого редактирования:', id);
        const response = await axios.get(`/api/manual/${id}`);
        console.log('✅ Мануал загружен:', response.data);
        
        setManual(response.data);
        setManualForm({
          title: response.data.titleFormatted || response.data.title,
          description: response.data.description,
          isPublic: response.data.isPublic
        });
        const itemsData = response.data.items || [];
        setItems(itemsData);
        setFilteredItems(itemsData);
        // Автоматически выбираем первый элемент, если есть
        if (itemsData.length > 0) {
          setSelectedItem(itemsData[0]);
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки мануала:', error);
      console.error('Детали ошибки:', error.response?.data);
      toast.error('Ошибка загрузки мануала');
    } finally {
      setLoading(false);
    }
  };

  const performSearch = (query, baseItems = items) => {
    if (!query.trim()) {
      setFilteredItems(baseItems);
      return;
    }
    const searchLower = query.toLowerCase();

    const searchInItem = (item) => {
      const titleMatch = (item.title || '').toLowerCase().includes(searchLower);
      const contentMatch = (item.content || '').toLowerCase().includes(searchLower);

      const matchingChildren = (item.children || [])
        .filter(child => child && child._id)
        .map(searchInItem)
        .filter(Boolean);

      if (titleMatch || contentMatch || matchingChildren.length > 0) {
        return { ...item, children: matchingChildren };
      }
      return null;
    };

    const filtered = (baseItems || []).map(searchInItem).filter(Boolean);
    setFilteredItems(filtered);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const scrollToElement = (itemId) => {
    setTimeout(() => {
      const element = document.getElementById(`item-${itemId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const toggleExpanded = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setEditingContent(item.content || '');
    setEditingTitle(item.title || '');
    setPreviewContent(item.content || '');
    
    // Находим родительский элемент (если это подраздел) и устанавливаем его как выбранный
    const findParentItem = (items, targetId) => {
      for (const currentItem of items) {
        if (currentItem.children) {
          const found = currentItem.children.some(child => child._id === targetId);
          if (found) {
            return currentItem; // Возвращаем родительский элемент
          }
          const parent = findParentItem(currentItem.children, targetId);
          if (parent) return parent;
        }
      }
      return null;
    };
    
    // Если это подраздел, выбираем родительский элемент, иначе сам элемент
    const parentItem = findParentItem(items, item._id);
    setSelectedItem(parentItem || item);
    
    // Раскрываем все родительские элементы для подразделов
    const expandParentItems = (items, targetId, expandedSet = new Set()) => {
      for (const currentItem of items) {
        if (currentItem._id === targetId) {
          return expandedSet;
        }
        if (currentItem.children) {
          const found = currentItem.children.some(child => child._id === targetId);
          if (found) {
            expandedSet.add(currentItem._id);
            expandParentItems(currentItem.children, targetId, expandedSet);
            return expandedSet;
          }
        }
      }
      return expandedSet;
    };
    
    // Получаем все родительские элементы, которые нужно раскрыть
    const parentsToExpand = expandParentItems(items, item._id);
    const newExpanded = new Set([...expandedItems, ...parentsToExpand]);
    setExpandedItems(newExpanded);
    
    // Прокручиваем к редактируемому элементу
    scrollToElement(item._id);
  };


  const uploadImage = async (file) => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await axios.post('/api/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data.path; // Возвращаем путь к загруженному изображению
    } catch (error) {
      console.error('Ошибка загрузки изображения:', error);
      toast.error('Ошибка загрузки изображения');
      return null;
    }
  };

  const insertImageAtCursor = async (imageSource) => {
    let imagePath = imageSource;
    
    // Если это файл (перетаскивание), загружаем его на сервер
    if (imageSource instanceof File) {
      imagePath = await uploadImage(imageSource);
      if (!imagePath) {
        return; // Если загрузка не удалась, выходим
      }
    }
    
    // Получаем активный contentEditable элемент
    const getActiveEditor = () => {
      const editors = document.querySelectorAll('[contenteditable]');
      console.log('🔍 Найдено contentEditable элементов:', editors.length);
      console.log('🔍 Активное поле:', activeField);
      
      const activeElement = document.activeElement;
      if (activeElement && activeElement.contentEditable === 'true') {
        console.log('🔍 Найден активный contentEditable элемент:', activeElement);
        return activeElement;
      }
      
      // Если нет активного элемента, используем поле контента
      if (activeField === 'content' && editors.length > 1) {
        return editors[1]; // Второй contentEditable - содержимое
      } else if (activeField === 'title' && editors.length > 0) {
        return editors[0]; // Первый contentEditable - заголовок
      } else {
        return editors[1] || editors[0]; // Fallback на содержимое или заголовок
      }
    };
    
    const editor = getActiveEditor();
    if (!editor) {
      console.log('❌ Не найден активный редактор');
      return;
    }
    
    console.log('🖼️ Вставляем изображение в редактор:', editor);
    console.log('🖼️ Путь к изображению:', imagePath);
    
    // Создаем markdown-ссылку для изображения (как для видео)
    const imageMarkdown = `![Изображение](${imagePath})`;
    
    // Добавляем изображение в конец текста (как для видео)
    const newContent = activeField === 'content' 
      ? editingContent + '\n' + imageMarkdown
      : editingItemTitle + '\n' + imageMarkdown;
    
    // Обновляем состояние
    if (activeField === 'content') {
      setEditingContent(newContent);
      setPreviewContent(newContent);
    } else {
      setEditingTitle(newContent);
    }
    
    toast.success('Изображение добавлено!');
  };

  const handleInsertImageUrl = async () => {
    if (!imageUrl.trim()) {
      toast.error(`Введите URL ${mediaType === 'image' ? 'изображения' : 'видео'} или выберите файл`);
      return;
    }
    
    if (mediaType === 'image') {
      // Если это URL изображения
      if (imageUrl.startsWith('http') || imageUrl.startsWith('data:')) {
        // Более гибкая проверка URL изображения
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
        const hasImageExtension = imageExtensions.some(ext => 
          imageUrl.toLowerCase().includes(ext)
        );
        
        // Проверяем, что это либо URL с расширением изображения, либо data URL, либо URL с query параметрами
        const isValidImageUrl = hasImageExtension || 
                               imageUrl.includes('data:image/') || 
                               imageUrl.includes('unsplash.com') ||
                               imageUrl.includes('imgur.com') ||
                               imageUrl.includes('github.com') ||
                               imageUrl.includes('cloudinary.com') ||
                               imageUrl.includes('amazonaws.com') ||
                               imageUrl.includes('?') || // URL с параметрами
                               imageUrl.includes('#'); // URL с якорем
        
        if (!isValidImageUrl) {
          toast.error('Пожалуйста, введите корректную ссылку на изображение');
          return;
        }
        
        await insertImageAtCursor(imageUrl);
        setImageUrl('');
        setShowImageModal(false);
        toast.success('Изображение добавлено в текст!');
      }
    } else if (mediaType === 'video') {
      // Если это URL видео
      if (imageUrl.startsWith('http')) {
        // Проверяем, что это поддерживаемый видеохостинг
        const isSupportedVideo = imageUrl.includes('youtube.com') || 
                                imageUrl.includes('youtu.be') || 
                                imageUrl.includes('vk.com/video') || 
                                imageUrl.includes('vkvideo.ru') || 
                                imageUrl.includes('rutube.ru') || 
                                imageUrl.includes('tiktok.com') || 
                                imageUrl.includes('dailymotion.com');
        
        if (!isSupportedVideo) {
          toast.error('Поддерживаются только: YouTube, VK Video, Rutube, TikTok, Dailymotion');
          return;
        }
        
        // Вставляем видео как markdown ссылку с названием
        let videoTitle = 'Видео';
        if (imageUrl.includes('youtube.com') || imageUrl.includes('youtu.be')) {
          videoTitle = 'YouTube видео';
        } else if (imageUrl.includes('vk.com/video') || imageUrl.includes('vkvideo.ru')) {
          videoTitle = 'VK видео';
        } else if (imageUrl.includes('rutube.ru')) {
          videoTitle = 'Rutube видео';
        } else if (imageUrl.includes('tiktok.com')) {
          videoTitle = 'TikTok видео';
        } else if (imageUrl.includes('dailymotion.com')) {
          videoTitle = 'Dailymotion видео';
        }
        
        const videoMarkdown = `[${videoTitle}](${imageUrl})`;
          const newContent = editingContent + '\n' + videoMarkdown;
          setEditingContent(newContent);
          setPreviewContent(newContent);
        
        setImageUrl('');
        setShowImageModal(false);
        toast.success('Видео добавлено в текст!');
      }
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      toast.success('Загружаем изображение...');
      
      try {
        await insertImageAtCursor(file);
        setImageUrl('');
        setShowImageModal(false);
        toast.success('Изображение добавлено в текст!');
      } catch (error) {
        console.error('Ошибка при добавлении изображения:', error);
        toast.error('Ошибка при добавлении изображения');
      }
    } else {
      toast.error('Выберите файл изображения');
    }
  };

  // Обработчики для drag & drop изображений
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0) {
      for (const file of imageFiles) {
        await insertImageAtCursor(file);
      }
    } else {
      toast.error('Перетащите файлы изображений');
    }
  };

  const handleSaveItem = async () => {
    if (!editingItemTitle.trim()) {
      toast.error('Название раздела не может быть пустым');
      return;
    }

    try {
      console.log('💾 Сохраняем раздел с содержимым:', editingContent);
      
      const response = await axios.put(`/api/manual/items/${editingItem._id}`, {
        title: cleanFontTags(editingItemTitle),
        content: cleanFontTags(editingContent)
      });
      
      console.log('💾 Ответ сервера:', response.data);
      console.log('💾 Содержимое сохраненного раздела:', response.data.content);
      
      // Обновляем локальное состояние
      const serverItem = response.data;
      
      // Собираем финальную версию узла:
      // приоритет у данных с сервера, но children оставляем из текущего editingItem,
      // если сервер не прислал детей (обычно так и есть на PUT)
      const mergedItem = {
        ...editingItem,
        ...serverItem,
        children: serverItem.children ?? editingItem.children ?? []
      };
      
      const updatedItems = updateItemInList(items, editingItem._id, mergedItem);
      
      // ⚠️ НЕ рассчитывай здесь на старый стейт!
      setItems(updatedItems);

      // Обнови filteredItems прямо по updatedItems
      performSearch(searchQuery, updatedItems);

      // Выбери selectedItem из НОВОГО дерева, чтобы ссылка совпадала
      const findItemById = (arr, id) => {
        for (const it of arr) {
          if (it._id === id) return it;
          const inChild = it.children && findItemById(it.children, id);
          if (inChild) return inChild;
        }
        return null;
      };
      const freshRef = findItemById(updatedItems, editingItem._id);
      setSelectedItem(freshRef || mergedItem);
      setEditingItem(null);
      setEditingContent('');
      setEditingTitle('');
      
      toast.success('Раздел обновлен');
    } catch (error) {
      console.error('Ошибка сохранения раздела:', error);
      toast.error('Ошибка сохранения раздела');
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditingContent('');
    setEditingTitle('');
    setPreviewContent('');
  };

  const handleAddItem = () => {
    setNewItemData({ title: '', content: '', parentId: null });
    setShowAddModal(true);
  };

  const handleAddSubItem = (parentId) => {
    setNewItemData({ title: '', content: '', parentId });
    setShowAddModal(true);
  };

  // Функция для поиска элемента в иерархии
  const findItemInHierarchy = (items, itemId, parentPath = []) => {
    for (let item of items) {
      if (item._id === itemId) {
        return { 
          item, 
          parent: parentPath.length > 0 ? parentPath[parentPath.length - 1] : null, 
          parentPath,
          items: parentPath.length > 0 ? parentPath[parentPath.length - 1].children : items 
        };
      }
      if (item.children && item.children.length > 0) {
        const found = findItemInHierarchy(item.children, itemId, [...parentPath, item]);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  // Функция для обновления элемента в иерархии
  const updateItemInHierarchy = (items, itemId, updatedItem) => {
    return items.map(item => {
      if (item._id === itemId) {
        return updatedItem;
      }
      if (item.children && item.children.length > 0) {
        return { ...item, children: updateItemInHierarchy(item.children, itemId, updatedItem) };
      }
      return item;
    });
  };

  // Функция для сериализации иерархии элементов
  const serializeItemsHierarchy = (items) => {
    return items
      .filter(item => item && item._id) // Фильтруем элементы без ID
      .map(item => ({
        _id: item._id,
        children: item.children && item.children.length > 0 
          ? serializeItemsHierarchy(item.children) 
          : []
      }));
  };

  // Функция для получения всех ID элементов всех уровней
  const getAllItemIds = (items) => {
    const ids = [];
    items.forEach(item => {
      if (item && item._id) {
        ids.push(item._id);
        if (item.children && item.children.length > 0) {
          ids.push(...getAllItemIds(item.children));
        }
      }
    });
    return ids;
  };

  // Функция для обработки drag & drop
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      try {
        // Находим элементы в иерархии
        const activeItem = findItemInHierarchy(manual.items, active.id);
        const overItem = findItemInHierarchy(manual.items, over.id);
        
        if (!activeItem || !overItem) {
          console.error('Не удалось найти элементы для перетаскивания');
          return;
        }

        // Если элементы на одном уровне (проверяем по родителю)
        if (activeItem.parent === overItem.parent) {
          const parentItems = activeItem.parent ? activeItem.parent.children : manual.items;
          const oldIndex = parentItems.findIndex(item => item._id === active.id);
          const newIndex = parentItems.findIndex(item => item._id === over.id);
          
          if (oldIndex === -1 || newIndex === -1) {
            console.error('❌ Не удалось найти элементы в родительском списке');
            return;
          }
          
          const newParentItems = arrayMove(parentItems, oldIndex, newIndex);
          
          // Обновляем иерархию
          let updatedItems;
          if (activeItem.parent) {
            // Обновляем дочерние элементы родителя
            updatedItems = updateItemInHierarchy(manual.items, activeItem.parent._id, {
              ...activeItem.parent,
              children: newParentItems
            });
          } else {
            // Обновляем основные элементы
            updatedItems = newParentItems;
          }
          
          // Обновляем локальное состояние
          setManual(prev => ({
            ...prev,
            items: updatedItems
          }));
          
          setFilteredItems(updatedItems);
          setItems(updatedItems);
          
          // Отправляем обновление на сервер с полной иерархией
          await axios.put(`/api/manual/${manual._id}`, {
            title: manual.title,
            description: manual.description,
            isPublic: manual.isPublic,
            items: serializeItemsHierarchy(updatedItems)
          });
          
          toast.success('Порядок элементов обновлен');
        } else {
          console.log('❌ Элементы на разных уровнях:', {
            activeParent: activeItem.parent?._id,
            overParent: overItem.parent?._id,
            activePath: activeItem.parentPath?.map(p => p._id),
            overPath: overItem.parentPath?.map(p => p._id)
          });
          toast.error('Можно перетаскивать только элементы одного уровня');
        }
      } catch (error) {
        console.error('Ошибка обновления порядка:', error);
        toast.error('Ошибка обновления порядка элементов');
        // Восстанавливаем исходное состояние при ошибке
        fetchManual();
      }
    }
  };

  const handleSaveManual = async () => {
    if (!manualForm.title.trim()) {
      toast.error('Введите название мануала');
      return;
    }

    try {
      const isNew = !id || id === 'new';
      
      const cleanedTitle = cleanFontTags(manualForm.title);
      console.log('🔍 Исходное название:', manualForm.title);
      console.log('🔍 Очищенное название:', cleanedTitle);
      console.log('🔍 Полная форма:', manualForm);
      console.log('🔍 HTML содержимое:', manualForm.title);
      
      // Извлекаем только текстовое содержимое для проверки длины
      const textOnly = manualForm.title.replace(/<[^>]*>/g, '').trim();
      console.log('🔍 Только текст (без HTML):', textOnly);
      console.log('🔍 Длина текста:', textOnly.length);
      
      // Проверяем, что все обязательные поля заполнены
      if (!textOnly || textOnly === '') {
        toast.error('Название мануала не может быть пустым');
        return;
      }
      
      if (textOnly.length > 100) {
        toast.error('Название мануала не может быть длиннее 100 символов');
        return;
      }
      
      if (isNew) {
        const dataToSend = {
          ...manualForm,
          title: textOnly, // Отправляем только текст для валидации
          titleFormatted: cleanedTitle // Сохраняем HTML в отдельном поле
        };
        console.log('📤 Отправляем данные на сервер:', dataToSend);
        
        const response = await axios.post('/api/manual', dataToSend);
        setManual(response.data);
        
        // ВАЖНО: Обновляем items и filteredItems с актуальными данными
        const itemsData = response.data.items || [];
        setItems(itemsData);
        setFilteredItems(itemsData);
        
        setEditingManual(false);
        toast.success('Мануал создан');
        // Перенаправляем на страницу редактирования созданного мануала
        window.history.replaceState(null, '', `/manual/${response.data._id}/edit`);
        // Обновляем переменную id для использования в других функциях
        window.location.reload(); // Простое решение - перезагружаем страницу
      } else {
        const dataToSend = {
          ...manualForm,
          title: textOnly, // Отправляем только текст для валидации
          titleFormatted: cleanedTitle // Сохраняем HTML в отдельном поле
        };
        console.log('📤 Обновляем мануал, отправляем данные:', dataToSend);
        
        const response = await axios.put(`/api/manual/${id}`, dataToSend);
        setManual(response.data);
        
        // ВАЖНО: Обновляем items и filteredItems с актуальными данными
        const itemsData = response.data.items || [];
        setItems(itemsData);
        setFilteredItems(itemsData);
        
        setEditingManual(false);
        toast.success('Мануал обновлен');
        
        // НЕ перезагружаем страницу, а просто обновляем состояние
        // window.location.reload();
      }
    } catch (error) {
      console.error('Ошибка сохранения мануала:', error);
      console.error('Детали ошибки:', error.response?.data);
      console.error('Статус:', error.response?.status);
      console.error('Заголовки:', error.response?.headers);
      
      if (error.response?.data?.errors) {
        console.error('Ошибки валидации:', error.response.data.errors);
        console.error('Детали каждой ошибки:', error.response.data.errors.map(e => ({ field: e.path, message: e.msg || e.message, value: e.value })));
        toast.error(`Ошибка валидации: ${error.response.data.errors.map(e => e.msg || e.message).join(', ')}`);
      } else {
        toast.error('Ошибка сохранения мануала');
      }
    }
  };

  const handleCancelEditManual = () => {
    if (!id || id === 'new') {
      // Если это новый мануал, перенаправляем на дашборд
      window.location.href = '/manual';
    } else {
      // Если редактируем существующий, возвращаем исходные данные
      setManualForm({
        title: manual.titleFormatted || manual.title,
        description: manual.description,
        isPublic: manual.isPublic
      });
      setEditingManual(false);
    }
  };

  const handleSaveNewItem = async () => {
    if (!newItemData.title.trim()) {
      toast.error('Введите название раздела');
      return;
    }

    // Используем ID мануала из состояния, а не из URL
    const manualId = manual?._id || id;
    
    if (!manualId || manualId === 'new') {
      toast.error('Сначала сохраните мануал');
      return;
    }

    try {
      const response = await axios.post(`/api/manual/${manualId}/items`, {
        ...newItemData,
        title: cleanFontTags(newItemData.title),
        content: cleanFontTags(newItemData.content)
      });
      
      // Обновляем мануал с данными с сервера
      if (response.data.manual) {
        setManual(response.data.manual);
        setItems(response.data.manual.items);
        setFilteredItems(response.data.manual.items);
        performSearch(searchQuery); // Обновляем результаты поиска
      }
      
      if (newItemData.parentId) {
        toast.success('Подраздел добавлен');
      } else {
        toast.success('Раздел добавлен');
      }
      
      setShowAddModal(false);
      setNewItemData({ title: '', content: '', parentId: null });
    } catch (error) {
      console.error('Ошибка добавления раздела:', error);
      toast.error('Ошибка добавления раздела');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот раздел?')) {
      return;
    }

    try {
      await axios.delete(`/api/manual/items/${itemId}`);
      const updatedItems = removeItemById(items, itemId);
      setItems(updatedItems);
      performSearch(searchQuery); // Обновляем результаты поиска
      if (selectedItem && selectedItem._id === itemId) {
        setSelectedItem(null);
      }
      toast.success('Раздел удален');
    } catch (error) {
      console.error('Ошибка удаления раздела:', error);
      toast.error('Ошибка удаления раздела');
    }
  };

  const updateItemInList = (items, itemId, updatedItem) => {
    return items.map(item => {
      if (item._id === itemId) {
        return {
          ...item,
          ...updatedItem,
          // если в updatedItem нет children — сохраняем существующие
          children: updatedItem.children ?? item.children ?? []
        };
      }
      if (item.children) {
        return { ...item, children: updateItemInList(item.children, itemId, updatedItem) };
      }
      return item;
    });
  };

  const updateItemWithChild = (items, parentId, newChild) => {
    return items.map(item => {
      if (item._id === parentId) {
        return { ...item, children: [...(item.children || []), newChild] };
      }
      if (item.children) {
        return { ...item, children: updateItemWithChild(item.children, parentId, newChild) };
      }
      return item;
    });
  };


  const renderItemContent = (item, level = 0) => {
    const isEditing = editingItem && editingItem._id === item._id;

    return (
      <div 
        key={item._id}
        id={`item-${item._id}`}
        className={`${level > 0 ? 'ml-6' : ''}`}
      >
        {level > 0 && <hr className="my-4 border-gray-200" />}
        
        <div className="py-4">
          {isEditing ? (
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Название раздела
                </label>
                
                {/* Поле ввода заголовка */}
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg">
                  <div
                    contentEditable
                    onInput={(e) => {
                      setEditingTitle(e.target.innerHTML);
                      setActiveField('title');
                    }}
                    onFocus={() => setActiveField('title')}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    suppressContentEditableWarning={true}
                    className="w-full p-4 border-0 resize-none focus:outline-none bg-white dark:bg-gray-600 dark:text-black min-h-[60px] text-xl font-bold"
                    style={{
                      minHeight: '60px',
                      outline: 'none'
                    }}
                    data-placeholder="Введите название раздела"
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Содержимое раздела
                </label>
                
                
                {/* Универсальная панель инструментов */}
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg">
                  <div className="border-b border-gray-300 dark:border-gray-600 p-2 bg-gray-50 dark:bg-gray-700 rounded-t-lg">
                    <div className="flex flex-wrap gap-2 items-center">
                      {/* Скрытые кнопки переключения (автоматическое переключение) */}
                      <div className="hidden">
                        <button onClick={() => setActiveField('title')}>Заголовок</button>
                        <button onClick={() => setActiveField('content')}>Содержимое</button>
                      </div>
                      <select 
                        className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                      value={selectedFont}
                        onChange={(e) => {
                          setSelectedFont(e.target.value);
                          if (e.target.value) {
                            applyFormatting('fontName', e.target.value);
                          }
                        }}
                      >
                        <option value="">Шрифт</option>
                        <option value="Arial, sans-serif">Arial</option>
                        <option value="Helvetica, sans-serif">Helvetica</option>
                        <option value="Times New Roman, serif">Times New Roman</option>
                        <option value="Georgia, serif">Georgia</option>
                        <option value="Verdana, sans-serif">Verdana</option>
                        <option value="Courier New, monospace">Courier New</option>
                        <option value="Roboto, sans-serif">Roboto</option>
                        <option value="Open Sans, sans-serif">Open Sans</option>
                        <option value="PT Sans, sans-serif">PT Sans</option>
                        <option value="PT Serif, serif">PT Serif</option>
                        <option value="Lora, serif">Lora</option>
                        <option value="Playfair Display, serif">Playfair Display</option>
                        <option value="Montserrat, sans-serif">Montserrat</option>
                        <option value="Raleway, sans-serif">Raleway</option>
                        <option value="Ubuntu, sans-serif">Ubuntu</option>
                      </select>
                      
                      <select 
                        className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                        value={selectedSize}
                        onChange={(e) => {
                          setSelectedSize(e.target.value);
                          if (e.target.value) {
                            const editor = getActiveEditor();
                            if (editor) {
                              editor.focus();
                              
                              const selection = window.getSelection();
                              const range = selection.getRangeAt(0);
                              
                              if (!range.collapsed) {
                                // Если есть выделение - применяем к выделенному тексту
                                const span = document.createElement('span');
                                span.style.fontSize = e.target.value;
                                try {
                                  range.surroundContents(span);
                                } catch (e) {
                                  range.insertNode(span);
                                }
                              } else {
                                // Если нет выделения - проверяем, есть ли уже span с размером шрифта в позиции курсора
                                const container = range.commonAncestorContainer;
                                let existingSpan = null;
                                
                                // Ищем существующий span с размером шрифта
                                if (container.nodeType === Node.TEXT_NODE) {
                                  const parent = container.parentNode;
                                  if (parent && parent.tagName === 'SPAN' && parent.style.fontSize) {
                                    existingSpan = parent;
                                  }
                                } else if (container.tagName === 'SPAN' && container.style.fontSize) {
                                  existingSpan = container;
                                }
                                
                                if (existingSpan) {
                                  // Обновляем размер существующего span
                                  existingSpan.style.fontSize = e.target.value;
                                  // Перемещаем курсор в конец span
                                  range.setStartAfter(existingSpan);
                                  range.collapse(true);
                                  selection.removeAllRanges();
                                  selection.addRange(range);
                                } else {
                                  // Создаем новый span только если его нет
                                  const span = document.createElement('span');
                                  span.style.fontSize = e.target.value;
                                  span.innerHTML = '&nbsp;';
                                  range.insertNode(span);
                                  // Перемещаем курсор внутрь span
                                  range.setStartAfter(span);
                                  range.collapse(true);
                                  selection.removeAllRanges();
                                  selection.addRange(range);
                                }
                              }
                              
                              // Обновляем состояние после применения размера
                              if (activeField === 'title') {
                                setEditingTitle(editor.innerHTML);
                              } else {
                                setEditingContent(editor.innerHTML);
                                setPreviewContent(editor.innerHTML);
                              }
                            }
                          }
                        }}
                      >
                        <option value="">Размер</option>
                        <option value="6px">6px</option>
                        <option value="8px">8px</option>
                        <option value="10px">10px</option>
                        <option value="12px">12px</option>
                        <option value="14px">14px</option>
                        <option value="16px">16px</option>
                        <option value="18px">18px</option>
                        <option value="20px">20px</option>
                        <option value="24px">24px</option>
                        <option value="28px">28px</option>
                        <option value="32px">32px</option>
                        <option value="36px">36px</option>
                        <option value="48px">48px</option>
                      </select>
                      
                      <button 
                        className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => applyFormatting('bold')}
                        title="Жирный"
                      >
                        B
                      </button>
                      
                      <button 
                        className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => applyFormatting('italic')}
                        title="Курсив"
                      >
                        I
                      </button>
                      
                      <button 
                        className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => applyFormatting('underline')}
                        title="Подчеркивание"
                      >
                        U
                      </button>
                      
                      <button 
                        className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => applyFormatting('insertUnorderedList')}
                        title="Маркированный список"
                      >
                        •
                      </button>
                      
                      <button 
                        className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => applyFormatting('insertOrderedList')}
                        title="Нумерованный список"
                      >
                        1.
                      </button>
                      
                      <button 
                        className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => applyFormatting('strikeThrough')}
                        title="Зачеркнутый текст"
                      >
                        <span style={{ textDecoration: 'line-through' }}>S</span>
                      </button>
                      
                      <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1"></div>
                      
                      {/* Кнопка цвета с выпадающей палитрой */}
                      <div className="relative color-palette-container">
                        <button 
                          className="w-8 h-8 border border-gray-300 dark:border-gray-600 rounded text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center bg-white dark:bg-gray-800"
                          onClick={() => setShowColorPalette(!showColorPalette)}
                          title={`Цвет текста: ${selectedColor}`}
                        >
                          <span 
                            className="text-lg font-bold"
                            style={{ color: selectedColor }}
                          >
                            A
                          </span>
                        </button>
                        
                        {/* Выпадающая палитра цветов */}
                        {showColorPalette && (
                          <div 
                            className="absolute top-full left-0 mt-1 p-4 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-10 w-48"
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            <div className="grid grid-cols-6 gap-1.5 mb-3">
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-black hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('foreColor', '#000000');
                                  setSelectedColor('#000000');
                                  setShowColorPalette(false);
                                }}
                                title="Черный"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-white hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('foreColor', '#ffffff');
                                  setSelectedColor('#ffffff');
                                  setShowColorPalette(false);
                                }}
                                title="Белый"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-red-500 hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('foreColor', '#ef4444');
                                  setSelectedColor('#ef4444');
                                  setShowColorPalette(false);
                                }}
                                title="Красный"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-orange-500 hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('foreColor', '#f97316');
                                  setSelectedColor('#f97316');
                                  setShowColorPalette(false);
                                }}
                                title="Оранжевый"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-yellow-500 hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('foreColor', '#eab308');
                                  setSelectedColor('#eab308');
                                  setShowColorPalette(false);
                                }}
                                title="Желтый"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-green-500 hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('foreColor', '#22c55e');
                                  setSelectedColor('#22c55e');
                                  setShowColorPalette(false);
                                }}
                                title="Зеленый"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-blue-500 hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('foreColor', '#3b82f6');
                                  setSelectedColor('#3b82f6');
                                  setShowColorPalette(false);
                                }}
                                title="Синий"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-purple-500 hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('foreColor', '#a855f7');
                                  setSelectedColor('#a855f7');
                                  setShowColorPalette(false);
                                }}
                                title="Фиолетовый"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-pink-500 hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('foreColor', '#ec4899');
                                  setSelectedColor('#ec4899');
                                  setShowColorPalette(false);
                                }}
                                title="Розовый"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-gray-500 hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('foreColor', '#6b7280');
                                  setSelectedColor('#6b7280');
                                  setShowColorPalette(false);
                                }}
                                title="Серый"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-indigo-500 hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('foreColor', '#6366f1');
                                  setSelectedColor('#6366f1');
                                  setShowColorPalette(false);
                                }}
                                title="Индиго"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-teal-500 hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('foreColor', '#14b8a6');
                                  setSelectedColor('#14b8a6');
                                  setShowColorPalette(false);
                                }}
                                title="Бирюзовый"
                              ></button>
                            </div>
                            
                            {/* Кастомный цвет */}
                            <div className="border-t border-gray-300 dark:border-gray-600 pt-2">
                              <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">Кастомный цвет:</label>
                              <input
                                type="color"
                                className="w-full h-8 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                                onMouseDown={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  applyFormatting('foreColor', e.target.value);
                                  setSelectedColor(e.target.value);
                                  // Не закрываем палитру автоматически для кастомного цвета
                                }}
                              />
                  </div>
                          </div>
                        )}
                </div>
                
                      {/* Кнопка цвета фона с выпадающей палитрой */}
                      <div className="relative bg-color-palette-container">
                        <button 
                          className="w-8 h-8 border border-gray-300 dark:border-gray-600 rounded text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center relative"
                          style={{ backgroundColor: selectedBgColor === 'transparent' ? 'white' : selectedBgColor }}
                          onClick={() => setShowBgColorPalette(!showBgColorPalette)}
                          title={`Цвет фона: ${selectedBgColor}`}
                        >
                          {selectedBgColor === 'transparent' && (
                            <div className="absolute inset-0 bg-gradient-to-br from-red-200 to-blue-200 opacity-50 rounded"></div>
                          )}
                          <span 
                            className="text-lg font-bold relative z-10"
                            style={{ 
                              color: selectedBgColor === '#ffffff' || selectedBgColor === '#ffff00' || selectedBgColor === 'transparent' ? '#000000' : '#ffffff'
                            }}
                          >
                            A
                          </span>
                        </button>
                        
                        {/* Выпадающая палитра цветов фона */}
                        {showBgColorPalette && (
                          <div 
                            className="absolute top-full left-0 mt-1 p-4 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-10 w-48"
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            <div className="grid grid-cols-6 gap-1.5 mb-3">
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-transparent hover:scale-110 transition-transform relative"
                                onClick={() => {
                                  applyFormatting('backColor', 'transparent');
                                  setSelectedBgColor('transparent');
                                  setShowBgColorPalette(false);
                                }}
                                title="Прозрачный фон"
                              >
                                <div className="absolute inset-0 bg-gradient-to-br from-red-200 to-blue-200 opacity-50 rounded"></div>
                                <div className="absolute inset-1 bg-white rounded-sm"></div>
                              </button>
                              
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-black hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('backColor', '#000000');
                                  setSelectedBgColor('#000000');
                                  setShowBgColorPalette(false);
                                }}
                                title="Черный фон"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-white hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('backColor', '#ffffff');
                                  setSelectedBgColor('#ffffff');
                                  setShowBgColorPalette(false);
                                }}
                                title="Белый фон"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-red-500 hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('backColor', '#ef4444');
                                  setSelectedBgColor('#ef4444');
                                  setShowBgColorPalette(false);
                                }}
                                title="Красный фон"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-orange-500 hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('backColor', '#f97316');
                                  setSelectedBgColor('#f97316');
                                  setShowBgColorPalette(false);
                                }}
                                title="Оранжевый фон"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-yellow-500 hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('backColor', '#eab308');
                                  setSelectedBgColor('#eab308');
                                  setShowBgColorPalette(false);
                                }}
                                title="Желтый фон"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-green-500 hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('backColor', '#22c55e');
                                  setSelectedBgColor('#22c55e');
                                  setShowBgColorPalette(false);
                                }}
                                title="Зеленый фон"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-blue-500 hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('backColor', '#3b82f6');
                                  setSelectedBgColor('#3b82f6');
                                  setShowBgColorPalette(false);
                                }}
                                title="Синий фон"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-purple-500 hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('backColor', '#a855f7');
                                  setSelectedBgColor('#a855f7');
                                  setShowBgColorPalette(false);
                                }}
                                title="Фиолетовый фон"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-pink-500 hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('backColor', '#ec4899');
                                  setSelectedBgColor('#ec4899');
                                  setShowBgColorPalette(false);
                                }}
                                title="Розовый фон"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-gray-500 hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('backColor', '#6b7280');
                                  setSelectedBgColor('#6b7280');
                                  setShowBgColorPalette(false);
                                }}
                                title="Серый фон"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-indigo-500 hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('backColor', '#6366f1');
                                  setSelectedBgColor('#6366f1');
                                  setShowBgColorPalette(false);
                                }}
                                title="Индиго фон"
                              ></button>
                              <button 
                                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-teal-500 hover:scale-110 transition-transform"
                                onClick={() => {
                                  applyFormatting('backColor', '#14b8a6');
                                  setSelectedBgColor('#14b8a6');
                                  setShowBgColorPalette(false);
                                }}
                                title="Бирюзовый фон"
                              ></button>
                            </div>
                            
                            {/* Кастомный цвет фона */}
                            <div className="border-t border-gray-300 dark:border-gray-600 pt-2">
                              <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">Кастомный цвет фона:</label>
                              <input
                                type="color"
                                className="w-full h-8 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                                onMouseDown={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  applyFormatting('backColor', e.target.value);
                                  setSelectedBgColor(e.target.value);
                                  // Не закрываем палитру автоматически для кастомного цвета
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1"></div>
                      
                      {/* Кнопки выравнивания */}
                      <button 
                        className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => applyFormatting('justifyLeft')}
                        title="Выровнять по левому краю"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="3" y1="6" x2="21" y2="6"/>
                          <line x1="3" y1="12" x2="15" y2="12"/>
                          <line x1="3" y1="18" x2="21" y2="18"/>
                        </svg>
                      </button>
                      
                      <button 
                        className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => applyFormatting('justifyCenter')}
                        title="Выровнять по центру"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="3" y1="6" x2="21" y2="6"/>
                          <line x1="9" y1="12" x2="15" y2="12"/>
                          <line x1="3" y1="18" x2="21" y2="18"/>
                        </svg>
                      </button>
                      
                      <button 
                        className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => applyFormatting('justifyRight')}
                        title="Выровнять по правому краю"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="3" y1="6" x2="21" y2="6"/>
                          <line x1="9" y1="12" x2="21" y2="12"/>
                          <line x1="3" y1="18" x2="21" y2="18"/>
                        </svg>
                      </button>
                  </div>
                </div>
                
                  <div
                    contentEditable
                    onInput={(e) => {
                      setEditingContent(e.target.innerHTML);
                      setPreviewContent(e.target.innerHTML);
                      setActiveField('content');
                    }}
                    onFocus={() => setActiveField('content')}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    suppressContentEditableWarning={true}
                    className="w-full p-4 border-0 resize-none focus:outline-none bg-white dark:bg-gray-600 dark:text-black min-h-[200px]"
                    style={{ 
                      minHeight: '200px',
                      outline: 'none'
                    }}
                    data-placeholder="Введите содержимое раздела. Для изображений: ![Описание](URL). Для видео: [Название](URL_видео). Поддерживаются: YouTube, VK, Rutube, TikTok, Dailymotion"
                  />
                </div>
                
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => setShowImageModal(true)}
                        className="flex items-center px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-md hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                      >
                        <Paperclip className="h-4 w-4" />
                      </button>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        💡 Добавьте изображения и видео: ![alt](url) для картинок, [название](url) для видео
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={handleCancelEdit}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
                      >
                        Отмена
                      </button>
                      <button
                        onClick={handleSaveItem}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
                      >
                        <Save className="h-4 w-4" />
                        <span>Сохранить</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
            </div>
          ) : (
            <div>
              <h2 className={`font-bold text-gray-900 dark:text-gray-100 mb-4 ${level === 0 ? 'text-2xl' : 'text-xl'}`}>
                <span dangerouslySetInnerHTML={{ __html: item.title }} />
              </h2>
              
              
              <SimpleContentRenderer content={item.content || ''} />
            </div>
          )}
          
          {/* Предварительный просмотр в режиме редактирования */}
          {isEditing && (
            <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 transition-all duration-300 ease-in-out">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                <span className="mr-2">👁️</span>
                Предварительный просмотр:
              </h4>
              
              <SimpleContentRenderer content={previewContent || editingContent || ''} />
              
              {(!previewContent && !editingContent) && (
                <div className="text-gray-400 dark:text-gray-500 text-sm italic">
                  Предварительный просмотр появится здесь, когда вы начнете вводить текст
                </div>
              )}
            </div>
          )}
        </div>

        {/* Рендерим подразделы */}
        {item.children && item.children.length > 0 && (
          <div className="space-y-4">
            {item.children.filter(child => child && child._id).map(child => renderItemContent(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const removeItemById = (list, itemId) => {
    return (list || []).reduce((acc, item) => {
      if (!item || item._id === itemId) return acc;
      const children = item.children ? removeItemById(item.children, itemId) : [];
      acc.push({ ...item, children });
      return acc;
    }, []);
  };

  const renderMenuItem = (item, level = 0, index = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item._id);
    const isSelected = selectedItem && selectedItem._id === item._id;
    
    
    // Для всех элементов используем SortableItem (drag & drop для всех уровней)
    return (
      <SortableItem
        key={item._id}
        item={item}
        level={level}
        index={index}
        onEdit={handleEditItem}
        onDelete={handleDeleteItem}
        onAddSubItem={handleAddSubItem}
        onSelect={setSelectedItem}
        onToggleExpanded={toggleExpanded}
        isExpanded={isExpanded}
        hasChildren={hasChildren}
        isSelected={isSelected}
        renderMenuItem={renderMenuItem}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!manual) {
    return (
      <div className="text-center py-12">
        <BookOpen className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Мануал не найден</h2>
        <p className="text-gray-600 dark:text-gray-400">Возможно, мануал был удален или у вас нет доступа.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm p-4 mb-6">
        <div className="flex justify-between items-center">
          <div className="flex-1">
            {editingManual ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Название мануала *
                  </label>
                  <input
                    type="text"
                    value={manualForm.title}
                    onChange={(e) => setManualForm({ ...manualForm, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    placeholder="Введите название мануала"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Описание
                  </label>
                  <textarea
                    value={manualForm.description}
                    onChange={(e) => setManualForm({ ...manualForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    rows="3"
                    placeholder="Введите описание мануала"
                  />
                </div>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={manualForm.isPublic}
                      onChange={(e) => setManualForm({ ...manualForm, isPublic: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Публичный мануал</span>
                  </label>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleSaveManual}
                    className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
                  >
                    <Save className="h-4 w-4" />
                    <span>{(!id || id === 'new') ? 'Создать мануал' : 'Сохранить'}</span>
                  </button>
                  <button
                    onClick={handleCancelEditManual}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
                  >
                    Отмена
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center space-x-4 mb-2">
                  {editingManualTitle ? (
                    <div className="flex-1">
                      {/* Панель форматирования для названия */}
                      <div className="border border-gray-300 dark:border-gray-600 rounded-lg mb-2">
                        <div className="border-b border-gray-300 dark:border-gray-600 p-2 bg-gray-50 dark:bg-gray-700 rounded-t-lg">
                          <div className="flex flex-wrap gap-2 items-center">
                            <select 
                              className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                              value={selectedFont}
                              onChange={(e) => {
                                setSelectedFont(e.target.value);
                                if (e.target.value) {
                                  applyFormatting('fontName', e.target.value);
                                }
                              }}
                            >
                              <option value="">Шрифт</option>
                              <option value="Arial, sans-serif">Arial</option>
                              <option value="Helvetica, sans-serif">Helvetica</option>
                              <option value="Times New Roman, serif">Times New Roman</option>
                              <option value="Georgia, serif">Georgia</option>
                              <option value="Verdana, sans-serif">Verdana</option>
                              <option value="Courier New, monospace">Courier New</option>
                              <option value="Roboto, sans-serif">Roboto</option>
                              <option value="Open Sans, sans-serif">Open Sans</option>
                              <option value="PT Sans, sans-serif">PT Sans</option>
                              <option value="PT Serif, serif">PT Serif</option>
                              <option value="Lora, serif">Lora</option>
                              <option value="Playfair Display, serif">Playfair Display</option>
                              <option value="Montserrat, sans-serif">Montserrat</option>
                              <option value="Raleway, sans-serif">Raleway</option>
                              <option value="Ubuntu, sans-serif">Ubuntu</option>
                            </select>
                            
                            <select 
                              className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                              value={selectedSize}
                              onChange={(e) => {
                                setSelectedSize(e.target.value);
                                if (e.target.value) {
                                  applyFormatting('fontSize', e.target.value);
                                }
                              }}
                            >
                              <option value="">Размер</option>
                              <option value="1">Очень маленький</option>
                              <option value="2">Маленький</option>
                              <option value="3">Обычный</option>
                              <option value="4">Средний</option>
                              <option value="5">Большой</option>
                              <option value="6">Очень большой</option>
                              <option value="7">Огромный</option>
                            </select>
                            
                            <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1"></div>
                            
                            <button 
                              className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                              onClick={() => applyFormatting('bold')}
                              title="Жирный"
                            >
                              <strong>B</strong>
                            </button>
                            
                            <button 
                              className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                              onClick={() => applyFormatting('italic')}
                              title="Курсив"
                            >
                              <em>I</em>
                            </button>
                            
                            <button 
                              className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                              onClick={() => applyFormatting('underline')}
                              title="Подчеркивание"
                            >
                              <u>U</u>
                            </button>
                            
                            <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1"></div>
                            
                            {/* Кнопка цвета текста */}
                            <div className="relative color-palette-container manual-title-color-palette">
                              <button 
                                className="w-8 h-8 border border-gray-300 dark:border-gray-600 rounded text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center bg-white dark:bg-gray-800"
                                onClick={() => setShowColorPalette(!showColorPalette)}
                                title={`Цвет текста: ${selectedColor}`}
                              >
                                <span 
                                  className="text-lg font-bold"
                                  style={{ color: selectedColor }}
                                >
                                  A
                                </span>
                              </button>
                              
                              {/* Выпадающая палитра цветов */}
                              {showColorPalette && (
                                <div 
                                  className="absolute top-full left-0 mt-1 p-4 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-10 w-48"
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="grid grid-cols-6 gap-1.5 mb-3">
                                    <button 
                                      className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-black hover:scale-110 transition-transform"
                                      onClick={() => {
                                        applyFormatting('foreColor', '#000000');
                                        setSelectedColor('#000000');
                                        setShowColorPalette(false);
                                      }}
                                      title="Черный"
                                    ></button>
                                    <button 
                                      className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-white hover:scale-110 transition-transform"
                                      onClick={() => {
                                        applyFormatting('foreColor', '#ffffff');
                                        setSelectedColor('#ffffff');
                                        setShowColorPalette(false);
                                      }}
                                      title="Белый"
                                    ></button>
                                    <button 
                                      className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-red-500 hover:scale-110 transition-transform"
                                      onClick={() => {
                                        applyFormatting('foreColor', '#ef4444');
                                        setSelectedColor('#ef4444');
                                        setShowColorPalette(false);
                                      }}
                                      title="Красный"
                                    ></button>
                                    <button 
                                      className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-orange-500 hover:scale-110 transition-transform"
                                      onClick={() => {
                                        applyFormatting('foreColor', '#f97316');
                                        setSelectedColor('#f97316');
                                        setShowColorPalette(false);
                                      }}
                                      title="Оранжевый"
                                    ></button>
                                    <button 
                                      className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-yellow-500 hover:scale-110 transition-transform"
                                      onClick={() => {
                                        applyFormatting('foreColor', '#eab308');
                                        setSelectedColor('#eab308');
                                        setShowColorPalette(false);
                                      }}
                                      title="Желтый"
                                    ></button>
                                    <button 
                                      className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-green-500 hover:scale-110 transition-transform"
                                      onClick={() => {
                                        applyFormatting('foreColor', '#22c55e');
                                        setSelectedColor('#22c55e');
                                        setShowColorPalette(false);
                                      }}
                                      title="Зеленый"
                                    ></button>
                                    <button 
                                      className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-blue-500 hover:scale-110 transition-transform"
                                      onClick={() => {
                                        applyFormatting('foreColor', '#3b82f6');
                                        setSelectedColor('#3b82f6');
                                        setShowColorPalette(false);
                                      }}
                                      title="Синий"
                                    ></button>
                                    <button 
                                      className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-purple-500 hover:scale-110 transition-transform"
                                      onClick={() => {
                                        applyFormatting('foreColor', '#a855f7');
                                        setSelectedColor('#a855f7');
                                        setShowColorPalette(false);
                                      }}
                                      title="Фиолетовый"
                                    ></button>
                                    <button 
                                      className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-pink-500 hover:scale-110 transition-transform"
                                      onClick={() => {
                                        applyFormatting('foreColor', '#ec4899');
                                        setSelectedColor('#ec4899');
                                        setShowColorPalette(false);
                                      }}
                                      title="Розовый"
                                    ></button>
                                    <button 
                                      className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-gray-500 hover:scale-110 transition-transform"
                                      onClick={() => {
                                        applyFormatting('foreColor', '#6b7280');
                                        setSelectedColor('#6b7280');
                                        setShowColorPalette(false);
                                      }}
                                      title="Серый"
                                    ></button>
                                    <button 
                                      className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-indigo-500 hover:scale-110 transition-transform"
                                      onClick={() => {
                                        applyFormatting('foreColor', '#6366f1');
                                        setSelectedColor('#6366f1');
                                        setShowColorPalette(false);
                                      }}
                                      title="Индиго"
                                    ></button>
                                    <button 
                                      className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-teal-500 hover:scale-110 transition-transform"
                                      onClick={() => {
                                        applyFormatting('foreColor', '#14b8a6');
                                        setSelectedColor('#14b8a6');
                                        setShowColorPalette(false);
                                      }}
                                      title="Бирюзовый"
                                    >                                    </button>
                                  </div>
                                  
                                  {/* Кастомный цвет */}
                                  <div className="border-t border-gray-300 dark:border-gray-600 pt-2">
                                    <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">Кастомный цвет:</label>
                                    <input
                                      type="color"
                                      className="w-full h-8 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => {
                                        applyFormatting('foreColor', e.target.value);
                                        setSelectedColor(e.target.value);
                                        setShowColorPalette(false);
                                      }}
                                      value={selectedColor}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div
                          ref={manualTitleRef}
                          contentEditable
                          suppressContentEditableWarning={true}
                          onInput={(e) => {
                            setManualForm({ ...manualForm, title: e.target.innerHTML });
                            setActiveField('title');
                          }}
                          onFocus={() => setActiveField('title')}
                          className="text-2xl font-bold text-gray-900 dark:text-gray-100 focus:outline-none px-4 py-3 min-h-[60px]"
                          style={{ outline: 'none' }}
                        />
                      </div>
                      
                      <div className="flex items-center space-x-2 mt-2">
                        <button
                          onClick={() => {
                            handleSaveManual();
                            setEditingManualTitle(false);
                          }}
                          className="px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 text-sm"
                        >
                          Сохранить
                        </button>
                        <button
                          onClick={() => {
                            setManualForm({ ...manualForm, title: manual.titleFormatted || manual.title });
                            setEditingManualTitle(false);
                          }}
                          className="px-3 py-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-gray-500 text-sm"
                        >
                          Отмена
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <h1 
                        className="text-2xl"
                        dangerouslySetInnerHTML={{ __html: manual.titleFormatted || manual.title }}
                        style={{ 
                          fontWeight: 'inherit',
                          color: 'inherit',
                          fontFamily: 'inherit',
                          fontSize: 'inherit'
                        }}
                      />
                      <button
                        onClick={() => {
                          setManualForm({ ...manualForm, title: manual.titleFormatted || manual.title });
                          setEditingManualTitle(true);
                          setActiveField('title');
                        }}
                        className="text-primary-600 hover:text-primary-700 p-1"
                        title="Редактировать название"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
                {manual.description && (
                  <p className="text-gray-600 dark:text-gray-400">{manual.description}</p>
                )}
                <div className="flex items-center space-x-4 mt-2">
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    manual.isPublic 
                      ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                  }`}>
                    {manual.isPublic ? 'Публичный' : 'Приватный'}
                  </span>
                </div>
              </div>
            )}
          </div>
          {!editingManual && (
            <div className="flex space-x-2 ml-4">
              <button
                onClick={handleAddItem}
                className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Добавить раздел</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Боковое меню */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sticky top-4">
            {/* Поиск */}
            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                placeholder="Поиск по разделам..."
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
            
            <div className="space-y-1">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={filteredItems ? getAllItemIds(filteredItems) : []}
                  strategy={verticalListSortingStrategy}
                >
                  {filteredItems && filteredItems.filter(item => item && item._id).map((item, index) => renderMenuItem(item, 0, index))}
                </SortableContext>
              </DndContext>
              {searchQuery && filteredItems.length === 0 && (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
                  Ничего не найдено по запросу "{searchQuery}"
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Основной контент */}
        <div className="lg:col-span-3">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            {selectedItem ? (
              <div>
                {renderItemContent(selectedItem)}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
                <p>Выберите раздел из меню слева для просмотра и редактирования содержимого.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Модальное окно для добавления нового раздела */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {newItemData.parentId ? 'Добавить подраздел' : 'Добавить новый раздел'}
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Название раздела
                </label>
                <input
                  type="text"
                  value={newItemData.title}
                  onChange={(e) => setNewItemData({ ...newItemData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Введите название раздела"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Содержимое раздела (необязательно)
                </label>
                <textarea
                  value={newItemData.content}
                  onChange={(e) => setNewItemData({ ...newItemData, content: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows="8"
                  placeholder="Введите содержимое раздела (можно оставить пустым)"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={handleSaveNewItem}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Добавить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для добавления изображения */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Добавить {mediaType === 'image' ? 'изображение' : 'видео'}
              </h3>
              <button
                onClick={() => {
                  setShowImageModal(false);
                  setImageUrl('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Переключатель типа медиа */}
              <div className="flex space-x-2 mb-4">
                <button
                  onClick={() => setMediaType('image')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    mediaType === 'image'
                      ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  🖼️ Изображение
                </button>
                <button
                  onClick={() => setMediaType('video')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    mediaType === 'video'
                      ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  🎥 Видео
                </button>
              </div>

              {mediaType === 'image' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Выберите файл изображения
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Выберите изображение с вашего компьютера (JPG, PNG, GIF, WebP)
                    </p>
                  </div>
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300 dark:border-gray-600" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">или</span>
                    </div>
                  </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  URL изображения
                </label>
                <input
                  type="url"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="https://example.com/image.jpg"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Введите ссылку на изображение в интернете
                </p>
              </div>

              {imageUrl && (
                <div className="mt-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">Предварительный просмотр:</p>
                  <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                    <img 
                      src={imageUrl} 
                      alt="Предварительный просмотр" 
                      className="max-w-full h-auto rounded-lg"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div style={{display: 'none'}} className="text-center text-gray-500 dark:text-gray-400">
                      Не удалось загрузить изображение
                    </div>
                  </div>
                </div>
              )}
                </>
              )}

              {mediaType === 'video' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    URL видео
                  </label>
                  <input
                    type="url"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Поддерживаются: YouTube, VK Video, Rutube, TikTok, Dailymotion
                  </p>
                  
                  {imageUrl && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">Предварительный просмотр:</p>
                      <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                        <VideoEmbed url={imageUrl} title="Предварительный просмотр" />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowImageModal(false);
                  setImageUrl('');
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={handleInsertImageUrl}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
              >
                {mediaType === 'image' ? <Image className="h-4 w-4" /> : <span>🎥</span>}
                <span>Добавить</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveEditManual;
